import { i18n as I18n, Namespace, TFunction } from "i18next";

import { FilterConfigConstKeys, FilterGroupsConstKeys, FilterKey } from "../../../commons/specs/filters";
import { ProductSpecKey, ProductType } from "../../../commons/specs/product";

type ModelYearTerm = "modelYear" | "releaseYear";

/**
 * Accept any `t` whose namespace tuple includes `"commons"`. This lets
 * components that load multiple namespaces (e.g. `useTranslation(["kiosk",
 * "commons"])`) reuse these helpers without widening the runtime contract:
 * the keys we resolve always live under the `commons:` namespace.
 *
 * We narrow back to `TFunction<["commons"]>` internally to keep i18next's
 * typed key inference working at the call sites.
 */
type CommonsTFunction<Ns extends Namespace> = "commons" extends (Ns extends readonly (infer N)[] ? N : Ns)
  ? TFunction<Ns extends readonly string[] ? Ns : [Extract<Ns, string>]>
  : never;

const asCommonsT = <Ns extends Namespace>(t: CommonsTFunction<Ns>): TFunction<["commons"]> =>
  t as unknown as TFunction<["commons"]>;

/**
 * Resolve the localized label for a filter.
 *
 * The model-year filter has two label variants depending on the dealer's
 * `customization.modelYearDisplay.term` setting; everything else uses the
 * filter's `title` translation key.
 */
export const getFilterLabel = <Type extends ProductType, Ns extends Namespace = ["commons"]>(
  t: CommonsTFunction<Ns>,
  productType: Type,
  filterKey: FilterKey<Type>,
  modelYearTerm: ModelYearTerm
): string => {
  const tCommons = asCommonsT(t);
  if (filterKey === ProductSpecKey.ModelYear) {
    const baseKey = `commons:productFilterConfig.${productType}.productFilter.${ProductSpecKey.ModelYear}` as const;
    if (modelYearTerm === "modelYear") {
      const modelYearTitleKey = `${baseKey}.title` as const;
      return tCommons(modelYearTitleKey);
    } else {
      const releaseYearTitleKey = `${baseKey}.releaseYearTitle` as const;
      return tCommons(releaseYearTitleKey);
    }
  } else {
    const titleKey = `commons:productFilterConfig.${productType}.productFilter.${String(filterKey)}.title` as keyof {
      [T in ProductType as `commons:productFilterConfig.${T}.productFilter.${FilterConfigConstKeys<T>}.title`]: null;
    };
    return tCommons(titleKey);
  }
};

/**
 * Resolve the optional localized description for a filter.
 *
 * Descriptions are optional in `productFilterConfig.json` — the typed `t()`
 * cannot express "may or may not exist", so we runtime-check via `i18n.exists`
 * and fall back to an empty string.
 */
export const getFilterDescription = <Type extends ProductType, Ns extends Namespace = ["commons"]>(
  t: CommonsTFunction<Ns>,
  i18n: Pick<I18n, "exists">,
  productType: Type,
  filterKey: FilterKey<Type>
): string => {
  const tCommons = asCommonsT(t);
  const descriptionKey =
    `commons:productFilterConfig.${productType}.productFilter.${String(filterKey)}.description` as keyof {
      [
        T in ProductType as `commons:productFilterConfig.${T}.productFilter.${FilterConfigConstKeys<T>}.description`
      ]: null;
    };

  // @ts-ignore -- t() is statically typed to only allow existing keys, but the description is optional in the productFilterConfig, so we need to check if the key exists before trying to translate it
  return i18n.exists(descriptionKey) ? (tCommons(descriptionKey) as string) : "";
};

/**
 * Resolve the localized label for a filter group.
 */
export const getFilterGroupLabel = <Type extends ProductType, Ns extends Namespace = ["commons"]>(
  t: CommonsTFunction<Ns>,
  productType: Type,
  groupKey: string
): string => {
  const tCommons = asCommonsT(t);
  const groupTitleKey = `commons:productFilterConfig.${productType}.productFilterGroup.${groupKey}` as keyof {
    [T in ProductType as `commons:productFilterConfig.${T}.productFilterGroup.${FilterGroupsConstKeys<T>}`]: null;
  };
  return tCommons(groupTitleKey);
};

/**
 * Resolve the localized label for a single filter value (e.g. a frame-type
 * option inside a filter modal).
 *
 * Filter-value title keys are optional in `productFilterConfig.json` — we
 * runtime-check via `i18n.exists` and fall back to an empty string.
 */
export const getFilterValueLabel = <Type extends ProductType, Ns extends Namespace = ["commons"]>(
  t: CommonsTFunction<Ns>,
  i18n: Pick<I18n, "exists">,
  productType: Type,
  filterKey: FilterKey<Type>,
  value: string
): string => {
  const tCommons = asCommonsT(t);
  const filterValueKey = `commons:productFilterConfig.${productType}.productFilter.${String(filterKey)}.filter.${value}.title`;
  // @ts-ignore -- t() is statically typed to only allow existing keys, but the filter-value title is optional in the productFilterConfig, so we need to check if the key exists before trying to translate it
  return i18n.exists(filterValueKey) ? (tCommons(filterValueKey) as string) : "";
};
